import jwtDecode from 'jwt-decode';

export type AuthenticationProvider = 'google' | 'microsoft';

export type SSOUserData = {
  userId: string;
  userEmail: string;
  token: string;
  authenticationProvider: AuthenticationProvider;
};

export interface JwtPayload {
  sub: string;
  email: string;
  [key: string]: any;
}

declare global {
  interface Window {
    blueConicClient?: {
      profile: {
        getProfile: () => {
          setValue: (key: string, value: string) => void;
        };
        updateProfile: () => void;
      };
    };
    google?: any;
    ms?: any;
  }
}

/**
 * Poll for window.google to be available
 * @param maxAttempts - Maximum number of polling attempts
 * @param interval - Interval between checks in milliseconds
 * @returns Promise that resolves when google is available or rejects on timeout
 */
export function pollForGoogle(maxAttempts = 15, interval = 200): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window.google !== 'undefined') {
      resolve();
      return;
    }

    let attempts = 0;

    const checkGoogle = () => {
      attempts += 1;

      if (typeof window.google !== 'undefined') {
        resolve();
        return;
      }

      if (attempts >= maxAttempts) {
        reject(new Error('Google SDK not loaded after maximum attempts'));
        return;
      }

      // Add random jitter to avoid thundering herd
      setTimeout(checkGoogle, interval + Math.random() * 50);
    };

    checkGoogle();
  });
}

/**
 * Poll for window.ms to be available
 * @param maxAttempts - Maximum number of polling attempts
 * @param interval - Interval between checks in milliseconds
 * @returns Promise that resolves when microsoft is available or rejects on timeout
 */
export function pollForMicrosoft(maxAttempts = 15, interval = 200): Promise<void> {
  return new Promise((resolve, reject) => {
    // Early exit if Microsoft is already available
    if (typeof window.ms !== 'undefined') {
      resolve();
      return;
    }

    let attempts = 0;

    const checkMicrosoft = () => {
      attempts += 1;

      if (typeof window.ms !== 'undefined') {
        resolve();
        return;
      }

      if (attempts >= maxAttempts) {
        reject(new Error('Microsoft SDK not loaded after maximum attempts'));
        return;
      }

      // Add jitter to prevent thundering herd
      setTimeout(checkMicrosoft, interval + Math.random() * 50);
    };

    checkMicrosoft();
  });
}

/**
 * Generate SHA256 hash of text
 * @param text - The text to be hashed
 * @returns The SHA256 hash of the text
 */
async function generateSHA256Hash(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Create cookies for BlueConic
 * @param token - The token to be stored in the cookie
 * @param userId - The user ID to be stored in the cookie
 * @param authenticationProvider - The authentication provider to be stored in the cookie
 * @param userEmail - The user email to be stored in the cookie
 */
function createCookies({
  token,
  userId,
  authenticationProvider,
  userEmail,
}: SSOUserData) {
  if (typeof document === 'undefined') return;

  document.cookie = `blueconic_auth_token=${token}; path=/; secure; samesite=strict`;
  document.cookie = `blueconic_auth_token_id=${userId}; path=/; secure; samesite=strict`;
  document.cookie = `blueconic_auth_provider=${authenticationProvider}; path=/; secure; samesite=strict`;
  document.cookie = `blueconic_auth_sha256_email=${userEmail}; path=/; secure; samesite=strict`;
}

/**
 * Create BlueConic profile and cookies
 * @param data - The data to be stored in the cookie
 */
async function createBlueConicProfile(data: SSOUserData) {
  if (!window.blueConicClient) {
    return;
  }

  const { userId, userEmail, authenticationProvider } = data;

  // Generate SHA256 hash of email
  const userEmailSHA256 = await generateSHA256Hash(userEmail);

  createCookies({ ...data, userEmail: userEmailSHA256 });

  const profile = window.blueConicClient.profile.getProfile();
  profile.setValue('email', userEmail);
  profile.setValue('email_address_sha256', userEmailSHA256);
  profile.setValue('sso_user_id', userId);
  profile.setValue('sso_provider', authenticationProvider);
  profile.setValue('registration_date', new Date().toISOString());
  profile.setValue('interactions_converted', `${authenticationProvider} oAuth`);
  profile.setValue('registration_url', window.location.href);
  window.blueConicClient.profile.updateProfile();
}

export async function handleOAuthSignIn(provider: AuthenticationProvider, response: any) {
  const data: JwtPayload = jwtDecode(response.credential);

  await createBlueConicProfile({
    authenticationProvider: provider,
    token: response.credential,
    userId: data.sub,
    userEmail: data.email,
  });
}
